/**
 * API Service
 *
 * HTTP client for Aerware Foundry API.
 */

import { getAccessToken, useAuthStore } from '@/stores/auth';
import type { APIResponse } from '@/types';

const API_BASE = `${import.meta.env.VITE_API_URL || ''}/api`;

class APIError extends Error {
  code: string;
  details?: Record<string, unknown>;

  constructor(code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'APIError';
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | string[] | undefined>;
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, ...init } = options;

  // Build URL with query params
  let url = `${API_BASE}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v));
        } else {
          searchParams.append(key, String(value));
        }
      }
    }
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Set headers
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }

  // Add auth token
  const token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, {
    ...init,
    headers,
  });

  const data: APIResponse<T> = await response.json();

  if (!response.ok || !data.success) {
    const error = data.error || { code: 'UNKNOWN', message: 'Unknown error' };

    // Handle unauthorized - logout user
    if (response.status === 401) {
      useAuthStore.getState().logout();
    }

    throw new APIError(error.code, error.message, error.details);
  }

  return data.data as T;
}

// ============================================================================
// Auth API
// ============================================================================

export const authAPI = {
  login: (email: string, password: string) =>
    request<{ accessToken: string; refreshToken: string; user: any; expiresIn: number }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  signup: (data: { email: string; password: string; name: string; companyName?: string }) =>
    request<{ user: any; requiresVerification?: boolean; accessToken?: string; refreshToken?: string; expiresIn?: number }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  verifyEmail: (email: string, code: string) =>
    request<{ user: any; accessToken: string; refreshToken: string; expiresIn: number }>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),

  resendVerification: (email: string) =>
    request<{ message: string }>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  refresh: (refreshToken: string) =>
    request<{ accessToken: string; refreshToken: string; expiresIn: number }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),

  forgotPassword: (email: string) =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, newPassword: string) =>
    request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),

  me: () => request<any>('/auth/me'),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  // OAuth URL generators (browser navigates to these directly — no /api prefix)
  oauthGoogleUrl: (redirectUri: string) =>
    `${import.meta.env.VITE_API_URL || ''}/auth/oauth/google?redirect_uri=${encodeURIComponent(redirectUri)}`,

  oauthMicrosoftUrl: (redirectUri: string) =>
    `${import.meta.env.VITE_API_URL || ''}/auth/oauth/microsoft?redirect_uri=${encodeURIComponent(redirectUri)}`,
};

// ============================================================================
// Operators API
// ============================================================================

export const operatorsAPI = {
  list: (params?: {
    page?: number;
    pageSize?: number;
    teamId?: string;
    jobId?: string;
    status?: string;
    search?: string;
  }) => request<any[]>('/operators', { params }),

  get: (id: string) => request<any>(`/operators/${id}`),

  create: (data: { jobId: string; teamId: string; name?: string }) =>
    request<any>('/operators', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { name?: string; status?: string }) =>
    request<any>(`/operators/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`/operators/${id}`, { method: 'DELETE' }),

  start: (id: string) =>
    request<any>(`/operators/${id}/start`, { method: 'POST' }),

  stop: (id: string) =>
    request<any>(`/operators/${id}/stop`, { method: 'POST' }),

  getDrives: (id: string) =>
    request<{
      state: Record<string, { drives: Record<string, number> }>;
      health: Record<string, number>;
      mode: string;
      trust: {
        internal: { score: number; status: string };
        external: { score: number };
      };
    }>(`/operators/${id}/drives`),

  runCycle: (id: string) =>
    request<Record<string, unknown>>(`/operators/${id}/cycle`, { method: 'POST' }),
};

// ============================================================================
// Tasks API
// ============================================================================

export const tasksAPI = {
  list: (params?: {
    page?: number;
    pageSize?: number;
    operatorId?: string;
    status?: string;
    priority?: string;
    type?: string;
  }) => request<any[]>('/tasks', { params }),

  get: (id: string) => request<any>(`/tasks/${id}`),

  create: (data: {
    type: string;
    description: string;
    operatorId?: string;
    priority?: string;
    deadline?: string;
    input?: Record<string, unknown>;
  }) =>
    request<any>('/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { priority?: string; deadline?: string }) =>
    request<any>(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  cancel: (id: string) =>
    request<any>(`/tasks/${id}/cancel`, { method: 'POST' }),

  execute: (id: string, operatorId: string, input?: Record<string, unknown>) =>
    request<any>(`/tasks/${id}/execute`, {
      method: 'POST',
      body: JSON.stringify({ operatorId, input }),
    }),

  /** Retry a blocked task after resolving the capability gap */
  retry: (id: string) =>
    request<any>(`/tasks/${id}/retry`, { method: 'POST' }),
};

// ============================================================================
// Alisha API
// ============================================================================

export const alishaAPI = {
  status: () => request<any>('/alisha/status'),

  command: (data: {
    type: string;
    content: string;
    target?: { type: string; id?: string };
  }) =>
    request<any>('/alisha/command', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  escalations: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    operatorId?: string;
    severity?: string;
  }) => request<any[]>('/alisha/escalations', { params }),

  getEscalation: (id: string) => request<any>(`/alisha/escalations/${id}`),

  acknowledgeEscalation: (id: string) =>
    request<any>(`/alisha/escalations/${id}/acknowledge`, { method: 'POST' }),

  resolveEscalation: (id: string, resolution: string) =>
    request<any>(`/alisha/escalations/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolution }),
    }),

  /** Send a chat message to Alisha (authenticated, calls Claude) */
  chat: (message: string, conversationHistory?: Array<{ role: string; content: string }>) =>
    request<{ message: string; tokensUsed: { input: number; output: number; total: number } }>('/iris/chat', {
      method: 'POST',
      body: JSON.stringify({ message, conversationHistory }),
    }),

  /** Load saved Alisha conversation */
  getConversation: () =>
    request<{ messages: Array<{ id: string; sender: string; text: string; time: string }> }>('/iris/conversation'),

  /** Save full Alisha conversation */
  saveConversation: (messages: Array<{ id: string; sender: string; text: string; time: string }>) =>
    request<{ saved: boolean }>('/iris/conversation', {
      method: 'PUT',
      body: JSON.stringify({ messages }),
    }),
};

// ============================================================================
// Health API
// ============================================================================

export const healthAPI = {
  status: () => request<any>('/health/detailed'),
  metrics: (params?: { metrics?: string[]; interval?: string }) =>
    request<any>('/metrics', { params }),
};

// ============================================================================
// OAuth Connections API
// ============================================================================

export const connectionsAPI = {
  /** List all available OAuth services */
  listServices: () =>
    request<{
      service: string;
      displayName: string;
      description: string;
      iconUrl?: string;
      connected: boolean;
    }[]>('/connections/services'),

  /** List user's OAuth connections */
  list: () =>
    request<{
      id: string;
      service: string;
      serviceEmail?: string;
      status: 'active' | 'expired' | 'revoked' | 'error';
      expiresAt?: string;
      lastUsedAt?: string;
      createdAt: string;
    }[]>('/connections'),

  /** Initiate OAuth flow - returns authUrl to redirect/popup */
  initiate: (service: string, redirectUri?: string) =>
    request<{ authUrl: string; state: string }>(`/connections/${service}/initiate`, {
      method: 'POST',
      body: JSON.stringify({ redirectUri }),
    }),

  /** Refresh an OAuth token */
  refresh: (service: string) =>
    request<{ refreshed: boolean }>(`/connections/${service}/refresh`, { method: 'POST' }),

  /** Disconnect an OAuth connection */
  disconnect: (service: string) =>
    request<{ disconnected: boolean }>(`/connections/${service}`, { method: 'DELETE' }),

  /** Grant operator access to a connection */
  grantOperatorAccess: (service: string, operatorId: string, permission?: 'read' | 'write' | 'admin') =>
    request<{ granted: boolean }>(`/connections/${service}/operators/${operatorId}`, {
      method: 'POST',
      body: JSON.stringify({ permission: permission || 'read' }),
    }),

  /** Revoke operator access from a connection */
  revokeOperatorAccess: (service: string, operatorId: string) =>
    request<{ revoked: boolean }>(`/connections/${service}/operators/${operatorId}`, { method: 'DELETE' }),
};

// ============================================================================
// Marketplace API
// ============================================================================

/**
 * Listing type classification:
 * - connector: MCP bridge to existing service (GitHub, Slack, etc.)
 * - service: Requires new external signup/subscription (Mailchimp, Twilio)
 * - infrastructure: Provisions cloud resources with ongoing cost
 * - ai_tool: AI-powered tool built for Foundry ecosystem
 */
export type MCPListingType = 'connector' | 'service' | 'infrastructure' | 'ai_tool';

export interface MCPListing {
  id: string;
  slug: string;
  name: string;
  version: string;
  description: string;
  longDescription?: string;

  // Author (flat fields from backend)
  authorId: string;
  authorName: string;
  authorType: 'official' | 'community' | 'partner';

  // Classification
  listingType: MCPListingType;

  // Categorization
  categories: string[];
  tags: string[];

  // Pricing
  pricing: {
    model: 'free' | 'per_call' | 'per_token' | 'subscription' | 'tiered';
    pricePerCall?: number;
    pricePerToken?: number;
    monthlyPrice?: number;
    freeCallsPerMonth?: number;
    freeTokensPerMonth?: number;
  };

  // Stats (flat fields from backend)
  installCount: number;
  rating: number;
  reviewCount: number;

  // Technical
  requiredSecrets: string[];
  connectionConfig: Record<string, unknown>;
  tools: Array<{ name: string; description: string; inputSchema: Record<string, unknown> }>;

  // Status
  status: 'draft' | 'pending_review' | 'published' | 'deprecated' | 'removed';
  publishedAt?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface MCPInstallation {
  id: string;
  listingId: string;
  listingName: string;
  version: string;
  companyId: string;
  installedBy: string;
  installedAt: string;
  // pending_setup: installed but missing required credentials (won't appear to operators)
  // active: fully configured and available to operators
  status: 'pending_setup' | 'active' | 'paused' | 'uninstalled';
  allowedTeams: string[] | '*';
  allowedJobs: string[] | '*';
  config?: Record<string, unknown>;
  secretsConfigured: string[];
}

// Extended install response when tool requires setup
export interface MCPInstallResult extends MCPInstallation {
  setupRequired?: {
    missingSecrets: string[];
    listingType: MCPListingType;
    message: string;
  };
}

export interface MCPUsageSummary {
  companyId: string;
  billingPeriod: string;
  totalCalls: number;
  totalTokens: number;
  totalCost: number;
  byListing: {
    listingId: string;
    listingName: string;
    calls: number;
    tokens: number;
    cost: number;
  }[];
  byOperator: {
    operatorId: string;
    operatorName: string;
    calls: number;
    tokens: number;
    cost: number;
  }[];
}

export const marketplaceAPI = {
  // Browse listings
  searchListings: (params?: {
    search?: string;
    categories?: string[];
    tags?: string[];
    status?: string;
    pricing?: string;
    limit?: number;
    offset?: number;
  }) =>
    request<{
      listings: MCPListing[];
      total: number;
      page: number;
      pageSize: number;
    }>('/marketplace/listings', { params }),

  getListing: (id: string) =>
    request<MCPListing>(`/marketplace/listings/${id}`),

  getListingBySlug: (slug: string) =>
    request<MCPListing>(`/marketplace/listings/by-slug/${slug}`),

  // Installations
  listInstallations: (params?: {
    status?: string;
    listingId?: string;
    includeAllStatuses?: boolean;
  }) =>
    request<MCPInstallation[]>('/marketplace/installations', { params }),

  getInstallation: (id: string) =>
    request<MCPInstallation>(`/marketplace/installations/${id}`),

  install: (
    listingId: string,
    options?: {
      allowedTeams?: string[] | '*';
      allowedJobs?: string[] | '*';
      config?: Record<string, unknown>;
    }
  ) =>
    request<MCPInstallResult>(`/marketplace/listings/${listingId}/install`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    }),

  uninstall: (listingId: string) =>
    request<void>(`/marketplace/listings/${listingId}/uninstall`, {
      method: 'DELETE',
    }),

  configureSecrets: (installationId: string, secrets: string[]) =>
    request<{ configured: boolean; secretCount: number; installation: MCPInstallation }>(
      `/marketplace/installations/${installationId}/secrets`,
      {
        method: 'POST',
        body: JSON.stringify({ secrets }),
      }
    ),

  // Usage & Billing
  getUsageSummary: (billingPeriod?: string) =>
    request<MCPUsageSummary>('/marketplace/usage/summary', {
      params: billingPeriod ? { billingPeriod } : undefined,
    }),

  getUsage: (params?: {
    listingId?: string;
    operatorId?: string;
    billingPeriod?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    request<any[]>('/marketplace/usage', { params }),

  // Submissions
  createSubmission: (data: {
    name: string;
    description: string;
    version: string;
    categories: string[];
    connectionConfig: Record<string, any>;
    tools: { name: string; description: string; inputSchema?: Record<string, any> }[];
    proposedPricing: { model: string; price?: number; trialDays?: number };
  }) =>
    request<any>('/marketplace/submissions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  submitForReview: (submissionId: string) =>
    request<any>(`/marketplace/submissions/${submissionId}/submit`, { method: 'POST' }),

  listSubmissions: () =>
    request<any[]>('/marketplace/submissions'),
};

// ============================================================================
// Company Core API
// ============================================================================

export const companyCoreAPI = {
  list: (params?: { type?: string; includeRestricted?: boolean }) =>
    request<any[]>('/company-core', { params }),

  getByType: (type: string) =>
    request<any>(`/company-core/by-type/${type}`),

  get: (id: string) =>
    request<any>(`/company-core/${id}`),

  create: (data: { type: string; title: string; content: string; structured?: Record<string, unknown>; restricted?: boolean }) =>
    request<any>('/company-core', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { title?: string; content?: string; structured?: Record<string, unknown>; restricted?: boolean }) =>
    request<any>(`/company-core/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  seed: () =>
    request<any[]>('/company-core/seed', { method: 'POST' }),

  exportPdf: async () => {
    const token = getAccessToken();
    const url = `${API_BASE}/company-core/export/pdf`;
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('PDF export failed');
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'Company_Core.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  },
};

// ============================================================================
// Onboarding API
// ============================================================================

export const onboardingAPI = {
  saveToCore: (data: {
    path: 'path_a' | 'path_b';
    pathAOutputs?: Record<string, unknown>;
    pathBOutputs?: Record<string, unknown>;
    pathBFormData?: Record<string, unknown>;
  }) =>
    request<{ savedSections: string[] }>('/onboarding/save-to-core', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Get current onboarding state from backend */
  getState: () =>
    request<{
      path: string | null;
      step: string;
      businessStage: string | null;
      formData: Record<string, unknown>;
      outputs: Record<string, unknown>;
      completedAt: string | null;
      isComplete: boolean;
    }>('/onboarding'),

  /** Update onboarding state on backend */
  updateState: (data: {
    path?: 'path_a' | 'path_b';
    step?: string;
    businessStage?: string;
    formData?: Record<string, unknown>;
    outputs?: Record<string, unknown>;
  }) =>
    request<{
      path: string | null;
      step: string;
      businessStage: string | null;
      formData: Record<string, unknown>;
      outputs: Record<string, unknown>;
      completedAt: string | null;
      isComplete: boolean;
    }>('/onboarding', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /** Mark onboarding as complete on the backend */
  complete: () =>
    request<{ completed: boolean }>('/onboarding/complete', { method: 'POST' }),

  /** Get saved conversation from server (requires auth) */
  getConversation: () =>
    request<{
      conversation: {
        path: 'path_a' | 'path_b';
        conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
        formData: Record<string, unknown> | null;
        lastUpdated: string;
      } | null;
    }>('/onboarding/conversation'),
};

// ============================================================================
// Credits API
// ============================================================================

export interface CreditBalance {
  companyId: string;
  monthlyCredits: number;
  monthlyRemaining: number;
  purchasedCredits: number;
  graceRemaining: number;
  tier: string;
  totalAvailable: number;
  displayAvailable: number; // Excludes grace credits (for UI display)
  monthlyResetDate: string | null;
}

export interface CreditLedgerEntry {
  id: string;
  companyId: string;
  userId: string | null;
  type: 'monthly_grant' | 'purchase' | 'consumption' | 'grace' | 'expiry' | 'refund';
  amount: number;
  balanceAfter: number;
  source: string | null;
  referenceId: string | null;
  description: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export const creditsAPI = {
  getBalance: () =>
    request<CreditBalance>('/credits/balance'),

  getHistory: (limit = 50, offset = 0) =>
    request<{ entries: CreditLedgerEntry[]; limit: number; offset: number }>('/credits/history', {
      params: { limit, offset },
    }),

  getSummary: (period?: string) =>
    request<{ totalConsumed: number; totalGranted: number; totalPurchased: number; period: string }>('/credits/summary', {
      params: period ? { period } : undefined,
    }),

  getPackages: () =>
    request<{ packages: Array<{ id: string; label: string; credits: number; price: number; priceCents: number }> }>('/credits/packages'),

  purchase: (packageType: string) =>
    request<{ checkoutUrl?: string; purchase?: unknown; balance?: CreditBalance }>('/credits/purchase', {
      method: 'POST',
      body: JSON.stringify({ packageType }),
    }),
};

// ============================================================================
// Files API
// ============================================================================

export interface FileRecord {
  id: string;
  companyId: string;
  userId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  s3Key: string;
  createdAt: string;
}

export const filesAPI = {
  upload: async (file: File): Promise<FileRecord> => {
    const token = getAccessToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/files/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new APIError(
        body?.error?.code || 'UPLOAD_FAILED',
        body?.error?.message || 'File upload failed'
      );
    }

    const json = await response.json();
    return json.data;
  },

  list: (limit = 50, offset = 0) =>
    request<{ files: FileRecord[] }>('/files', {
      params: { limit, offset },
    }),

  getById: (id: string) =>
    request<FileRecord>(`/files/${id}`),

  download: async (id: string, filename?: string) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/files/${id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new APIError('DOWNLOAD_FAILED', 'File download failed');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  delete: (id: string) =>
    request<{ deleted: boolean }>(`/files/${id}`, {
      method: 'DELETE',
    }),

  storage: () =>
    request<{ bytesUsed: number }>('/files/storage'),
};

// ============================================================================
// Artifacts API
// ============================================================================

export interface ArtifactRecord {
  id: string;
  companyId: string;
  userId: string;
  fileId: string;
  type: string;
  category: string;
  title: string;
  source: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export const artifactsAPI = {
  list: (params?: { category?: string; type?: string }) =>
    request<{ artifacts: ArtifactRecord[] }>('/artifacts', { params }),

  getById: (id: string) =>
    request<ArtifactRecord>(`/artifacts/${id}`),

  download: async (id: string, filename?: string) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/artifacts/${id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new APIError('DOWNLOAD_FAILED', 'Artifact download failed');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  delete: (id: string) =>
    request<{ deleted: boolean }>(`/artifacts/${id}`, {
      method: 'DELETE',
    }),

  generateCompanyCorePdf: () =>
    request<ArtifactRecord>('/artifacts/generate/company-core-pdf', {
      method: 'POST',
    }),

  generateRoadmap: (opts?: { projectIds?: string[]; teamId?: string }) =>
    request<{ artifactId: string; title: string }>('/artifacts/generate/roadmap', {
      method: 'POST',
      body: JSON.stringify(opts || {}),
    }),

  generateStatusReport: (opts?: { periodStart?: string; periodEnd?: string; teamId?: string }) =>
    request<{ artifactId: string; title: string }>('/artifacts/generate/status-report', {
      method: 'POST',
      body: JSON.stringify(opts || {}),
    }),

  generateMeetingAgenda: (meetingId: string) =>
    request<{ artifactId: string; title: string }>('/artifacts/generate/meeting-agenda', {
      method: 'POST',
      body: JSON.stringify({ meetingId }),
    }),

  generateCustom: (templateId: string, opts?: { periodStart?: string; periodEnd?: string }) =>
    request<{ artifactId: string; title: string }>('/artifacts/generate/custom', {
      method: 'POST',
      body: JSON.stringify({ templateId, ...opts }),
    }),

  /**
   * Generate a document with specified type and format
   *
   * @param type - Document type (roadmap, status_report, meeting_agenda, business_plan, okr_document, team_charter, quarterly_review)
   * @param format - Output format (pdf, pptx, xlsx, docx). Defaults to 'pdf'
   * @param opts - Additional options
   */
  generate: (
    type: 'roadmap' | 'status_report' | 'meeting_agenda' | 'business_plan' | 'okr_document' | 'team_charter' | 'quarterly_review',
    format?: 'pdf' | 'pptx' | 'xlsx' | 'docx',
    opts?: {
      projectIds?: string[];
      teamId?: string;
      meetingId?: string;
      periodStart?: string;
      periodEnd?: string;
    }
  ) =>
    request<{ artifactId: string; title: string }>('/artifacts/generate', {
      method: 'POST',
      body: JSON.stringify({ type, format: format || 'pdf', ...opts }),
    }),
};

// ============================================================================
// Document Templates API
// ============================================================================

export const documentTemplatesAPI = {
  list: () =>
    request<{ templates: import('@/types').DocumentTemplate[] }>('/document-templates'),

  getById: (id: string) =>
    request<import('@/types').DocumentTemplate>(`/document-templates/${id}`),

  create: (data: import('@/types').DocumentTemplateInput) =>
    request<import('@/types').DocumentTemplate>('/document-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<import('@/types').DocumentTemplateInput>) =>
    request<import('@/types').DocumentTemplate>(`/document-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ deleted: boolean }>(`/document-templates/${id}`, {
      method: 'DELETE',
    }),
};

// ============================================================================
// Analytics API
// ============================================================================

export interface AnalyticsEvent {
  id: string;
  event: string;
  userId: string;
  companyId: string;
  properties: Record<string, unknown>;
  createdAt: string;
}

export const analyticsAPI = {
  getEvents: (params?: {
    event?: string;
    userId?: string;
    companyId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) =>
    request<{ events: AnalyticsEvent[]; limit: number; offset: number }>('/analytics/events', { params }),

  getFunnel: (events: string[], days = 30) =>
    request<{ stages: { event: string; count: number }[]; days: number }>('/analytics/funnel', {
      params: { events: events.join(','), days },
    }),

  getUsageByOperator: (period?: string) =>
    request<{ usage: Array<{ operatorId: string; creditsConsumed: number; taskCount: number }>; period: string }>('/analytics/usage-by-operator', {
      params: period ? { period } : undefined,
    }),
};

// ============================================================================
// Settings API
// ============================================================================

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'pending';
  avatar?: string;
}

export interface AccountLimits {
  currentUsers: number;
  maxUsers: number;
  tier: string;
}

export const settingsAPI = {
  // Notifications
  getNotifications: () =>
    request<{ emailSettings: Record<string, boolean>; exchangeSettings: Record<string, boolean> }>('/settings/notifications'),

  updateNotifications: (data: { emailSettings: Record<string, boolean>; exchangeSettings: Record<string, boolean> }) =>
    request<{ success: boolean }>('/settings/notifications', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Team members
  getTeamMembers: (params?: { search?: string; page?: number; pageSize?: number }) =>
    request<{ members: TeamMember[]; total: number }>('/settings/team/members', { params }),

  getTeamLimits: () =>
    request<AccountLimits>('/settings/team/limits'),

  inviteMember: (email: string) =>
    request<{ invitation: { id: string; email: string } }>('/settings/team/invitations', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  updateMemberRole: (memberId: string, role: string) =>
    request<{ success: boolean }>(`/settings/team/members/${memberId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  // Profile
  getProfile: () =>
    request<{ name: string; email: string; avatar?: string; role: string }>('/settings/profile'),

  updateProfile: (data: { name?: string }) =>
    request<{ success: boolean }>('/settings/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Email change
  requestEmailChange: (newEmail: string) =>
    request<{ message: string }>('/settings/email/change', {
      method: 'POST',
      body: JSON.stringify({ newEmail }),
    }),

  verifyEmailChange: (code: string) =>
    request<{ success: boolean }>('/settings/email/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  // Password
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ success: boolean }>('/settings/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  // Company reset (free tier only)
  resetCompany: () =>
    request<{ message: string; redirectUrl: string }>('/settings/company/reset', {
      method: 'POST',
    }),
};

// ============================================================================
// Knowledge Base API
// ============================================================================

export interface KnowledgeDocumentData {
  id: string;
  companyId: string;
  scope: 'company' | 'team' | 'job';
  scopeRef?: string;
  section: string;
  title: string;
  content: string;
  chunks: any[];
  metadata: {
    source: string;
    tags: string[];
    restricted?: boolean;
    version?: number;
    lastUpdatedBy?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export const knowledgeAPI = {
  list: (params?: {
    scope?: string;
    section?: string;
    source?: string;
    tags?: string[];
    restricted?: boolean;
    limit?: number;
    offset?: number;
  }) => request<KnowledgeDocumentData[]>('/knowledge/documents', { params }),

  get: (id: string) =>
    request<KnowledgeDocumentData>(`/knowledge/documents/${id}`),

  create: (data: {
    scope: 'company' | 'team' | 'job';
    scopeRef?: string;
    section: string;
    title: string;
    content: string;
    metadata?: { source?: string; tags?: string[]; restricted?: boolean };
  }) =>
    request<KnowledgeDocumentData>('/knowledge/documents', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: {
    title?: string;
    content?: string;
    metadata?: { tags?: string[]; restricted?: boolean };
  }) =>
    request<KnowledgeDocumentData>(`/knowledge/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`/knowledge/documents/${id}`, { method: 'DELETE' }),

  search: (data: {
    query: string;
    scope?: string;
    sections?: string[];
    limit?: number;
    minScore?: number;
  }) =>
    request<any[]>('/knowledge/query', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  stats: () =>
    request<{
      totalDocuments: number;
      bySection: Record<string, number>;
      byScope: Record<string, number>;
      totalContentLength: number;
      restrictedCount: number;
    }>('/knowledge/stats'),

  syncHistory: (limit?: number) =>
    request<any[]>('/knowledge/sync-history', { params: { limit } }),

  triggerSync: () =>
    request<{ message: string }>('/knowledge/sync', { method: 'POST' }),
};

// ============================================================================
// Meetings API
// ============================================================================

export interface MeetingData {
  id: string;
  companyId: string;
  type: string;
  title: string;
  scheduledFor: string;
  duration: number;
  status: string;
  organizer: { id: string; name: string; role: string };
  participants: { id: string; name: string; role: string }[];
  teamId?: string;
  agenda?: string[];
  createdAt: string;
  updatedAt: string;
}

export const meetingsAPI = {
  list: (params?: { status?: string; type?: string; from?: string; to?: string }) =>
    request<{ meetings: MeetingData[] }>('/meetings', { params }),

  get: (id: string) =>
    request<MeetingData>(`/meetings/${id}`),

  create: (data: {
    type: string;
    title: string;
    scheduledFor: string;
    duration?: number;
    teamId?: string;
    agenda?: string[];
    participants?: { id: string; name: string; role: string }[];
  }) =>
    request<MeetingData>('/meetings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, any>) =>
    request<MeetingData>(`/meetings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`/meetings/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// Standup API (Daily Alisha Briefings)
// ============================================================================

export const standupAPI = {
  /** Start or resume a standup session */
  start: (config?: { timezone?: string }) =>
    request<{ session: any; resumed: boolean }>('/standup/start', {
      method: 'POST',
      body: JSON.stringify({ config }),
    }),

  /** Send message in standup conversation */
  message: (sessionId: string, message: string) =>
    request<{ message: any }>(`/standup/${sessionId}/message`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),

  /** Complete standup session */
  complete: (sessionId: string, rating?: number, feedback?: string) =>
    request<{ session: any }>(`/standup/${sessionId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ rating, feedback }),
    }),

  /** Get active standup session */
  getActive: () =>
    request<{ session: any | null }>('/standup/active'),

  /** Get standup session by ID */
  get: (sessionId: string) =>
    request<{ session: any }>(`/standup/${sessionId}`),

  /** Get standup history */
  history: (limit?: number) =>
    request<{ sessions: any[] }>('/standup/history', {
      params: limit ? { limit } : undefined,
    }),
};

// ============================================================================
// Calendar / Roadmap API
// ============================================================================

export const milestonesAPI = {
  list: (params?: { projectId?: string; status?: string; startDate?: string; endDate?: string }) =>
    request<any[]>('/calendar/milestones', { params }),

  upcoming: (days?: number) =>
    request<any[]>('/calendar/milestones/upcoming', { params: days ? { days } : undefined }),

  get: (id: string) =>
    request<any>(`/calendar/milestones/${id}`),

  create: (data: {
    name: string;
    dueDate: string;
    projectId?: string;
    description?: string;
    status?: string;
    priority?: string;
    dependencies?: string[];
    businessImpact?: number;
  }) =>
    request<any>('/calendar/milestones', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, any>) =>
    request<any>(`/calendar/milestones/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ deleted: boolean }>(`/calendar/milestones/${id}`, { method: 'DELETE' }),
};

export const sprintsAPI = {
  list: (params?: { teamId?: string; status?: string; startDate?: string; endDate?: string }) =>
    request<any[]>('/calendar/sprints', { params }),

  current: (teamId?: string) =>
    request<any>('/calendar/sprints/current', { params: teamId ? { teamId } : undefined }),

  get: (id: string) =>
    request<any>(`/calendar/sprints/${id}`),

  create: (data: {
    number: number;
    startDate: string;
    endDate: string;
    teamId?: string;
    name?: string;
    status?: string;
    goals?: string[];
    capacity?: number;
  }) =>
    request<any>('/calendar/sprints', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, any>) =>
    request<any>(`/calendar/sprints/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ deleted: boolean }>(`/calendar/sprints/${id}`, { method: 'DELETE' }),

  metrics: () =>
    request<import('@/types').SprintMetrics[]>('/calendar/sprints/metrics'),
};

export const projectsAPI = {
  list: () =>
    request<any[]>('/calendar/projects'),

  get: (id: string) =>
    request<any>(`/calendar/projects/${id}`),

  create: (data: {
    name: string;
    description?: string;
    status?: string;
    color?: string;
    startDate?: string;
    targetDate?: string;
  }) =>
    request<any>('/calendar/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, any>) =>
    request<any>(`/calendar/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ deleted: boolean }>(`/calendar/projects/${id}`, { method: 'DELETE' }),
};

export const blackoutsAPI = {
  list: () =>
    request<any[]>('/calendar/blackouts'),

  active: () =>
    request<any[]>('/calendar/blackouts/active'),

  create: (data: {
    name: string;
    startDate: string;
    endDate: string;
    description?: string;
    restrictions?: string[];
    recurring?: boolean;
    recurrenceRule?: string;
  }) =>
    request<any>('/calendar/blackouts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ deleted: boolean }>(`/calendar/blackouts/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Provisioning
// ---------------------------------------------------------------------------

export interface ProvisioningPlan {
  tier: string;
  stageName: string;
  required: Array<{ jobId: string; teamId: string; name: string }>;
  flexPool: Array<{ jobId: string; teamId: string; name: string }>;
  defaultFlex: { jobId: string; teamId: string; name: string } | null;
  maxAgents: number;
}

export const provisioningAPI = {
  plan: (tier: string) =>
    request<ProvisioningPlan>(`/billing/provisioning/plan/${tier}`),

  execute: (companyId: string, tier: string) =>
    request<{ created: string[]; skipped: string[]; errors: string[] }>('/billing/provisioning/execute', {
      method: 'POST',
      body: JSON.stringify({ companyId, tier }),
    }),

  status: () =>
    request<{ currentOperators: number; requiredJobIds: string[] }>('/billing/provisioning/status'),
};

// ============================================================================
// Teams API
// ============================================================================

export interface Team {
  id: string;
  companyId: string;
  name: string;
  description: string | null;
  color: string;
  operatorCount: number;
  createdAt: string;
  updatedAt: string;
}

export const teamsAPI = {
  list: () =>
    request<Team[]>('/teams'),

  create: (data: { name: string; description?: string; color?: string }) =>
    request<Team>('/teams', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { name?: string; description?: string; color?: string }) =>
    request<Team>(`/teams/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ deleted: boolean }>(`/teams/${id}`, {
      method: 'DELETE',
    }),

  reassignOperator: (teamId: string, operatorId: string) =>
    request<{ teamId: string; operatorId: string }>(`/teams/${teamId}/operators/${operatorId}`, {
      method: 'PATCH',
    }),
};

export const orgChartAPI = {
  getTree: () => request<{ root: import('@/types').OrgChartNode; lastUpdated: string; companyId: string }>('/org-chart/tree'),
};

// ============================================================================
// Company Skills API (Anthropic Agent Skills)
// ============================================================================

export type CompanySkillType = 'knowledge' | 'procedure' | 'capability' | 'constraint' | 'persona';

export interface CompanySkill {
  id: string;
  companyId: string;
  type: CompanySkillType;
  name: string;
  description: string;
  content: string;
  priority: number;
  triggerConditions: {
    always?: boolean;
    taskTypes?: string[];
    keywords?: string[];
    roles?: string[];
    teams?: string[];
  };
  enabled: boolean;
  version: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SkillAssignment {
  id: string;
  skillId: string;
  teamId: string | null;
  operatorId: string | null;
  priorityOverride: number | null;
  createdAt: string;
}

export interface CompanySkillWithAssignments extends CompanySkill {
  assignments: SkillAssignment[];
}

export interface SkillStats {
  total: number;
  byType: Record<CompanySkillType, number>;
  enabled: number;
  disabled: number;
  totalTokens: number;
}

export const companySkillsAPI = {
  // List all skills
  list: (filters?: { type?: CompanySkillType; enabled?: boolean; search?: string }) =>
    request<{ skills: CompanySkill[]; count: number }>('/company-skills', {
      params: {
        type: filters?.type,
        enabled: filters?.enabled !== undefined ? String(filters.enabled) : undefined,
        search: filters?.search,
      },
    }),

  // Get skill stats
  stats: () => request<SkillStats>('/company-skills/stats'),

  // Get single skill with assignments
  get: (id: string) =>
    request<{ skill: CompanySkillWithAssignments }>(`/company-skills/${id}`),

  // Create skill
  create: (data: {
    type: CompanySkillType;
    name: string;
    description?: string;
    content: string;
    priority?: number;
    triggerConditions?: CompanySkill['triggerConditions'];
    enabled?: boolean;
  }) =>
    request<{ success: boolean; skill: CompanySkill }>('/company-skills', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update skill
  update: (id: string, data: Partial<{
    name: string;
    description: string;
    content: string;
    priority: number;
    triggerConditions: CompanySkill['triggerConditions'];
    enabled: boolean;
    version: string;
  }>) =>
    request<{ success: boolean; skill: CompanySkill }>(`/company-skills/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Delete skill
  delete: (id: string) =>
    request<{ success: boolean; message: string }>(`/company-skills/${id}`, {
      method: 'DELETE',
    }),

  // Toggle skill enabled/disabled
  toggle: (id: string) =>
    request<{ success: boolean; skill: CompanySkill }>(`/company-skills/${id}/toggle`, {
      method: 'POST',
    }),

  // Assign skill to team or operator
  assign: (id: string, data: { teamId?: string; operatorId?: string; priorityOverride?: number }) =>
    request<{ success: boolean; assignment: SkillAssignment }>(`/company-skills/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Remove assignment
  removeAssignment: (skillId: string, assignmentId: string) =>
    request<{ success: boolean; message: string }>(`/company-skills/${skillId}/assignments/${assignmentId}`, {
      method: 'DELETE',
    }),

  // Seed default Aerware branding skills
  seedDefaults: () =>
    request<{ success: boolean; message: string; skills: CompanySkill[] }>('/company-skills/seed', {
      method: 'POST',
    }),

  // Seed procedure skills (Alisha, documents, quick wins)
  seedProcedures: () =>
    request<{ success: boolean; message: string; skills: CompanySkill[] }>('/company-skills/seed-procedures', {
      method: 'POST',
    }),

  // Seed all default skills (branding + procedures)
  seedAll: () =>
    request<{ success: boolean; message: string; skills: CompanySkill[] }>('/company-skills/seed-all', {
      method: 'POST',
    }),
};

// ============================================================================
// Swarm API
// ============================================================================

export interface SwarmOperator {
  operatorId: string;
  jobId: string;
  displayName: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
  tokensUsed?: number;
}

export interface Swarm {
  id: string;
  name: string;
  type: 'task' | 'project' | 'permanent';
  teamId: string;
  status: 'active' | 'paused' | 'completed' | 'terminated';
  operators: SwarmOperator[];
  operatorCount?: number;
  parentOperatorId?: string;
  inheritedBoundaries?: {
    canModify: string[];
    cannotModify: string[];
    requiresApproval: string[];
  };
  ttlSeconds?: number;
  expiresAt?: string;
  swarmTokenBudget?: number;
  tokensUsed: number;
  taskContext?: string;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
}

export interface SwarmTask {
  id: string;
  swarmId: string;
  description: string;
  pattern: 'parallel' | 'sequential' | 'broadcast' | 'pipeline';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  subtasks: Array<{
    operatorId: string;
    content: string;
    status: string;
  }>;
  subtaskCount?: number;
  completedCount?: number;
  results: Array<{ success: boolean; result?: unknown; error?: string }>;
  aggregatedResult?: unknown;
  createdAt: string;
  completedAt?: string;
}

export interface SwarmTierLimits {
  maxSwarms: number;
  maxOperatorsPerSwarm: number;
  maxTokenBudget: number;
  allowedTypes: Array<'task' | 'project' | 'permanent'>;
}

export const swarmAPI = {
  // List all swarms
  list: () =>
    request<{ swarms: Swarm[]; count: number }>('/swarm'),

  // Get active swarms
  getActive: () =>
    request<{ swarms: Swarm[]; count: number }>('/swarm/active'),

  // Get swarm by ID
  get: (id: string) =>
    request<{ swarm: Swarm }>(`/swarm/${id}`),

  // Spawn a new swarm
  spawn: (data: {
    name: string;
    type: 'task' | 'project' | 'permanent';
    teamId: string;
    operators: Array<{ jobId: string; displayName?: string; count?: number }>;
    parentOperatorId?: string;
    ttlSeconds?: number;
    boundaryOverrides?: {
      canModify?: string[];
      cannotModify?: string[];
      requiresApproval?: string[];
    };
    swarmTokenBudget?: number;
    taskContext?: string;
  }) =>
    request<{ success: boolean; swarm: Swarm }>('/swarm/spawn', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Terminate a swarm
  terminate: (id: string) =>
    request<{ success: boolean; message: string }>(`/swarm/${id}`, {
      method: 'DELETE',
    }),

  // Get tier limits
  getLimits: (tier?: string) =>
    request<{ tier: string; limits: SwarmTierLimits }>('/swarm/limits', {
      params: tier ? { tier } : undefined,
    }),

  // Execute task across swarm
  executeTask: (swarmId: string, data: {
    description: string;
    pattern: 'parallel' | 'sequential' | 'broadcast' | 'pipeline';
    assignments: Array<{ operatorId: string; content: string; context?: Record<string, unknown> }>;
  }) =>
    request<{ success: boolean; task: SwarmTask }>(`/swarm/${swarmId}/execute`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Auto-assign task to swarm operators
  autoAssign: (swarmId: string, data: {
    content: string;
    pattern?: 'parallel' | 'sequential' | 'broadcast' | 'pipeline';
  }) =>
    request<{ success: boolean; task: SwarmTask }>(`/swarm/${swarmId}/auto-assign`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Split task and execute across operators
  splitExecute: (swarmId: string, data: {
    mainTask: string;
    splitInstructions: string[];
  }) =>
    request<{ success: boolean; task: SwarmTask }>(`/swarm/${swarmId}/split`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Get all tasks for a swarm
  getTasks: (swarmId: string) =>
    request<{ tasks: SwarmTask[]; count: number }>(`/swarm/${swarmId}/tasks`),

  // Get task details
  getTask: (swarmId: string, taskId: string) =>
    request<{ task: SwarmTask }>(`/swarm/${swarmId}/tasks/${taskId}`),

  // Cancel a running task
  cancelTask: (swarmId: string, taskId: string) =>
    request<{ success: boolean; message: string }>(`/swarm/${swarmId}/tasks/${taskId}/cancel`, {
      method: 'POST',
    }),
};

// ============================================================================
// Spending/Approvals API
// ============================================================================

export interface SpendingConfig {
  autoApproveBelowCents: number;
  perServiceLimitCents: number;
  monthlyLimitCents: number;
  currentMonthSpendCents: number;
  currentMonth: string;
  notifyOnAutoApprove: boolean;
  notifyOn80Percent: boolean;
  remainingBudgetCents: number;
}

export interface ServiceApproval {
  id: string;
  companyId: string;
  serviceName: string;
  listingId?: string;
  listingType?: string;
  pricingModel?: string;
  estimatedMonthlyCents?: number;
  setupCostCents?: number;
  approvalType: 'conversational' | 'explicit' | 'auto';
  approvedBy?: string;
  approvedInTask?: string;
  approvedInMessage?: string;
  spendingLimitCents?: number;
  status: 'active' | 'expired' | 'revoked';
  expiresAt?: string;
  createdAt: string;
}

export interface ApprovalRequest {
  id: string;
  companyId: string;
  serviceName: string;
  listingId?: string;
  listingType?: string;
  pricingModel: string;
  estimatedMonthlyCents?: number;
  setupCostCents?: number;
  requestedBy: string;
  taskId?: string;
  reason?: string;
  status: 'pending' | 'approved' | 'denied';
  decidedBy?: string;
  decidedAt?: string;
  spendingLimitCents?: number;
  createdAt: string;
  expiresAt?: string;
}

export interface Subscription {
  id: string;
  companyId: string;
  serviceName: string;
  listingId?: string;
  billingType: 'one_time' | 'monthly' | 'annual' | 'usage';
  costCents: number;
  nextBillingDate?: string;
  currentPeriodUsageCents?: number;
  spendingLimitCents?: number;
  status: 'pending_setup' | 'active' | 'paused' | 'cancelled';
  apiKeyConfigured: boolean;
  createdAt: string;
}

export interface CategoryApproval {
  category: string;
  maxMonthlyCents: number;
  autoApprove: boolean;
  enabled: boolean;
}

export const spendingAPI = {
  // Get spending configuration
  getConfig: () =>
    request<{ config: SpendingConfig }>('/spending/config'),

  // Update spending configuration (admin only)
  updateConfig: (data: {
    autoApproveBelowCents?: number;
    perServiceLimitCents?: number;
    monthlyLimitCents?: number;
    notifyOnAutoApprove?: boolean;
    notifyOn80Percent?: boolean;
  }) =>
    request<{ config: SpendingConfig }>('/spending/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Record a conversational approval
  recordApproval: (data: {
    serviceName: string;
    listingId?: string;
    listingType?: string;
    pricingModel?: string;
    estimatedMonthlyCents?: number;
    taskId?: string;
    messageId?: string;
    spendingLimit?: number;
  }) =>
    request<{ approval: ServiceApproval }>('/spending/approvals', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // List active approvals
  listApprovals: () =>
    request<{ approvals: ServiceApproval[] }>('/spending/approvals'),

  // Revoke an approval (admin only)
  revokeApproval: (id: string) =>
    request<{ message: string }>(`/spending/approvals/${id}`, {
      method: 'DELETE',
    }),

  // Get pending approval requests
  getRequests: () =>
    request<{ requests: ApprovalRequest[] }>('/spending/requests'),

  // Approve a request (admin only)
  approveRequest: (id: string, spendingLimitCents?: number) =>
    request<{ approval: ServiceApproval }>(`/spending/requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ spendingLimitCents }),
    }),

  // Deny a request (admin only)
  denyRequest: (id: string) =>
    request<{ message: string }>(`/spending/requests/${id}/deny`, {
      method: 'POST',
    }),

  // Get category approval settings
  getCategories: () =>
    request<{ categories: CategoryApproval[] }>('/spending/categories'),

  // Set category approval (admin only)
  setCategory: (data: {
    category: string;
    maxMonthlyCents: number;
    autoApprove: boolean;
    enabled: boolean;
  }) =>
    request<{ category: CategoryApproval }>('/spending/categories', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Get upcoming subscription renewals
  getSubscriptions: (daysAhead?: number) =>
    request<{ subscriptions: Subscription[] }>('/spending/subscriptions', {
      params: daysAhead ? { daysAhead: String(daysAhead) } : undefined,
    }),

  // Check if a service can be approved
  checkApproval: (data: {
    serviceName: string;
    listingId?: string;
    estimatedMonthlyCents: number;
  }) =>
    request<{
      canApprove: boolean;
      approvalType: 'pre_approved' | 'category' | 'auto' | 'needs_approval';
      reason?: string;
    }>('/spending/check', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ============================================================================
// Goals API (Quarterly Goals, Weekly Focus, Strategic Context)
// ============================================================================

export type GoalCategory = 'revenue' | 'growth' | 'product' | 'operations' | 'team' | 'customer' | 'other';
export type GoalStatus = 'draft' | 'active' | 'at_risk' | 'completed' | 'abandoned';
export type MetricSource = 'manual' | 'operator_report' | 'system' | 'integration';

export interface QuarterlyGoal {
  id: string;
  companyId: string;
  quarter: string;
  year: number;
  quarterNumber: 1 | 2 | 3 | 4;
  title: string;
  description?: string;
  category: GoalCategory;
  targetMetric?: string;
  targetValue?: number;
  currentValue?: number;
  unit?: string;
  status: GoalStatus;
  confidence: number;
  linkedMilestones: string[];
  linkedProjects: string[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface FocusItem {
  id: string;
  text: string;
  completed: boolean;
  linkedGoalId?: string;
}

export interface WeeklyReflection {
  completionNotes?: string;
  blockers?: string;
  nextWeekPriorities?: string;
  addedBy: 'founder' | 'alisha';
  addedAt: string;
}

export interface WeeklyFocus {
  id: string;
  companyId: string;
  weekOf: string;
  focusItems: FocusItem[];
  linkedGoals: string[];
  createdAt: string;
  updatedAt: string;
  reflections?: WeeklyReflection[];
}

export interface GoalSummary {
  quarter: string;
  goals: QuarterlyGoal[];
  weeklyFocus: WeeklyFocus | null;
  overallProgress: number;
  atRiskCount: number;
  completedCount: number;
}

export const goalsAPI = {
  // Quarterly Goals
  list: (quarter?: string) =>
    request<{ goals: QuarterlyGoal[] }>('/goals', { params: quarter ? { quarter } : undefined }),

  getActive: () =>
    request<{ goals: QuarterlyGoal[] }>('/goals/active'),

  get: (goalId: string) =>
    request<{ goal: QuarterlyGoal }>(`/goals/${goalId}`),

  create: (data: {
    title: string;
    description?: string;
    category?: GoalCategory;
    targetMetric?: string;
    targetValue?: number;
    unit?: string;
    status?: GoalStatus;
    confidence?: number;
    linkedMilestones?: string[];
    linkedProjects?: string[];
    quarterOverride?: string;
  }) =>
    request<{ goal: QuarterlyGoal }>('/goals', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (goalId: string, data: Partial<{
    title: string;
    description: string;
    category: GoalCategory;
    targetMetric: string;
    targetValue: number;
    unit: string;
    status: GoalStatus;
    confidence: number;
    linkedMilestones: string[];
    linkedProjects: string[];
  }>) =>
    request<{ goal: QuarterlyGoal }>(`/goals/${goalId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  updateProgress: (goalId: string, currentValue: number, source?: MetricSource, note?: string) =>
    request<{ goal: QuarterlyGoal }>(`/goals/${goalId}/progress`, {
      method: 'POST',
      body: JSON.stringify({ currentValue, source, note }),
    }),

  delete: (goalId: string) =>
    request<{ deleted: boolean }>(`/goals/${goalId}`, {
      method: 'DELETE',
    }),

  getMetrics: (goalId: string, limit?: number) =>
    request<{ metrics: Array<{ id: string; value: number; source: MetricSource; createdAt: string; note?: string }> }>(
      `/goals/${goalId}/metrics`,
      { params: limit ? { limit } : undefined }
    ),

  // Weekly Focus
  getWeeklyFocus: (weekOf?: string) =>
    request<{ focus: WeeklyFocus | null }>('/goals/weekly-focus', {
      params: weekOf ? { weekOf } : undefined,
    }),

  setWeeklyFocus: (data: {
    weekOf?: string;
    focusItems: Array<{ text: string; completed?: boolean; linkedGoalId?: string }>;
    linkedGoals?: string[];
  }) =>
    request<{ focus: WeeklyFocus }>('/goals/weekly-focus', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateFocusItem: (itemId: string, completed: boolean, weekOf?: string) =>
    request<{ focus: WeeklyFocus }>(`/goals/weekly-focus/item/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify({ completed, weekOf }),
    }),

  addReflection: (reflection: { completionNotes?: string; blockers?: string; nextWeekPriorities?: string }, weekOf?: string) =>
    request<{ focus: WeeklyFocus }>('/goals/weekly-focus/reflection', {
      method: 'POST',
      body: JSON.stringify({ ...reflection, weekOf }),
    }),

  getWeeklyHistory: (limit?: number) =>
    request<{ history: WeeklyFocus[] }>('/goals/weekly-focus/history', {
      params: limit ? { limit } : undefined,
    }),

  // Summary & Context
  getSummary: () =>
    request<{ summary: GoalSummary }>('/goals/summary'),

  getStrategicContext: () =>
    request<{ context: string }>('/goals/strategic-context'),
};

// ============================================================================
// Customers API
// ============================================================================

export type CustomerStatus = 'lead' | 'prospect' | 'qualified' | 'active' | 'inactive' | 'churned' | 'archived';
export type CompanySize = 'solo' | '1-10' | '11-50' | '51-200' | '201-500' | '500+';
export type AnnualRevenue = 'under-100k' | '100k-500k' | '500k-1m' | '1m-5m' | '5m-10m' | '10m+';
export type InteractionType = 'email' | 'call' | 'meeting' | 'note' | 'task' | 'deal';
export type InteractionOutcome = 'positive' | 'neutral' | 'negative' | 'follow_up_needed';

export interface Customer {
  id: string;
  companyId: string;
  name: string;
  email?: string;
  phone?: string;
  website?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactRole?: string;
  industry?: string;
  companySize?: CompanySize;
  annualRevenue?: AnnualRevenue;
  status: CustomerStatus;
  source?: string;
  assignedTo?: string;
  lifetimeValue: number;
  projectsCount: number;
  tasksCount: number;
  notes?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  firstContactAt?: string;
  lastContactAt?: string;
  convertedAt?: string;
}

export interface CustomerInteraction {
  id: string;
  customerId: string;
  companyId: string;
  type: InteractionType;
  subject?: string;
  content?: string;
  outcome?: InteractionOutcome;
  linkedTaskId?: string;
  linkedProjectId?: string;
  linkedMeetingId?: string;
  createdBy: string;
  occurredAt: string;
  createdAt: string;
}

export interface CustomerStats {
  total: number;
  byStatus: Record<CustomerStatus, number>;
  totalLifetimeValue: number;
  recentlyActive: number;
}

export const customersAPI = {
  // CRUD
  list: (options?: {
    status?: CustomerStatus;
    industry?: string;
    assignedTo?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) =>
    request<{ customers: Customer[] }>('/customers', { params: options }),

  get: (customerId: string) =>
    request<{ customer: Customer }>(`/customers/${customerId}`),

  create: (data: {
    name: string;
    email?: string;
    phone?: string;
    website?: string;
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    contactRole?: string;
    industry?: string;
    companySize?: CompanySize;
    annualRevenue?: AnnualRevenue;
    status?: CustomerStatus;
    source?: string;
    assignedTo?: string;
    notes?: string;
    tags?: string[];
  }) =>
    request<{ customer: Customer }>('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (customerId: string, data: Partial<{
    name: string;
    email: string;
    phone: string;
    website: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    contactRole: string;
    industry: string;
    companySize: CompanySize;
    annualRevenue: AnnualRevenue;
    status: CustomerStatus;
    source: string;
    assignedTo: string;
    notes: string;
    tags: string[];
    lifetimeValue: number;
  }>) =>
    request<{ customer: Customer }>(`/customers/${customerId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (customerId: string) =>
    request<{ deleted: boolean }>(`/customers/${customerId}`, { method: 'DELETE' }),

  // Interactions
  addInteraction: (customerId: string, data: {
    type: InteractionType;
    subject?: string;
    content?: string;
    outcome?: InteractionOutcome;
    linkedTaskId?: string;
    linkedProjectId?: string;
    linkedMeetingId?: string;
    occurredAt?: string;
  }) =>
    request<{ interaction: CustomerInteraction }>(`/customers/${customerId}/interactions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getInteractions: (customerId: string, limit?: number) =>
    request<{ interactions: CustomerInteraction[] }>(`/customers/${customerId}/interactions`, {
      params: limit ? { limit } : undefined,
    }),

  // Stats
  getStats: () =>
    request<{ stats: CustomerStats }>('/customers/stats'),

  // Linking
  linkProject: (customerId: string, projectId: string) =>
    request<{ linked: boolean }>(`/customers/${customerId}/link-project`, {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    }),

  linkTask: (customerId: string, taskId: string) =>
    request<{ linked: boolean }>(`/customers/${customerId}/link-task`, {
      method: 'POST',
      body: JSON.stringify({ taskId }),
    }),
};

// ============================================================================
// Usage API
// ============================================================================

export const usageAPI = {
  /** Check Alisha message quota (current, limit, remaining, resetDate) */
  checkAlishaQuota: () =>
    request<{
      current: number;
      limit: number;
      remaining: number;
      resetDate: string;
    }>('/usage/alisha-check'),
};

// ============================================================================
// Voice API
// ============================================================================

export const voiceAPI = {
  /** Initiate WebRTC call to operator */
  initiateCall: (operatorId: string) =>
    request<{
      callId: string;
      sessionId: string;
      iceServers: RTCIceServer[];
    }>('/voice/initiate', {
      method: 'POST',
      body: JSON.stringify({ operatorId }),
    }),

  /** End voice call */
  endCall: (callId: string) =>
    request<{ success: boolean }>(`/voice/calls/${callId}/end`, {
      method: 'POST',
    }),

  /** Get call history */
  getCallHistory: (limit?: number) =>
    request<{
      calls: VoiceCall[];
    }>('/voice/calls', {
      params: limit ? { limit } : undefined,
    }),

  /** Get call transcript */
  getTranscript: (callId: string) =>
    request<{
      transcript: TranscriptEntry[];
    }>(`/voice/calls/${callId}/transcript`),

  /** Get operator voice profile */
  getOperatorVoiceProfile: (operatorId: string) =>
    request<{
      profile: OperatorVoiceProfile;
    }>(`/voice/operators/${operatorId}/profile`),

  /** Request outbound call from operator (requires approval) */
  requestCallFromOperator: (params: {
    operatorId: string;
    reason: string;
    urgency?: 'low' | 'medium' | 'high' | 'critical';
  }) =>
    request<{
      requestId: string;
      autoApproved: boolean;
      callId?: string;
    }>('/voice/request-call', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  /** Approve outbound call request */
  approveCallRequest: (requestId: string) =>
    request<{
      approved: boolean;
      callId: string;
    }>(`/voice/requests/${requestId}/approve`, {
      method: 'POST',
    }),

  /** Decline outbound call request */
  declineCallRequest: (requestId: string) =>
    request<{
      declined: boolean;
    }>(`/voice/requests/${requestId}/decline`, {
      method: 'POST',
    }),
};

// Voice types
interface VoiceCall {
  id: string;
  operatorId: string;
  operatorName?: string;
  type: 'phone_inbound' | 'phone_outbound' | 'webrtc';
  status: string;
  startedAt: string;
  endedAt?: string;
  durationSeconds?: number;
  creditsConsumed?: number;
}

interface TranscriptEntry {
  role: 'user' | 'operator';
  text: string;
  timestamp: string;
  confidence?: number;
}

interface OperatorVoiceProfile {
  operatorId: string;
  phoneNumber?: string;
  phoneEnabled: boolean;
  voiceProvider: 'openai' | 'elevenlabs';
  voiceId: string;
  availableForCalls: boolean;
}

interface RTCIceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export { APIError };
